import { translate } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { OptionsGroup } from '@thoughtspot/radiant-react/widgets/select/base-select';
import _ from 'lodash';
import {
    ColumnTimeBucket,
    ColumnValueType,
    FalconDataType,
    Filter,
    FilterColumn,
    FilterContentInput,
    FilterTypes,
} from '/@services/generated/graphql-types';
import {
    getAppliedFilterIds,
    getDateFromEpoch,
    getEpochFromDate,
} from '../date-picker/date-filter.util';

const logger = create('date-range-filter-util');

export enum DateFilterOperators {
    ON = 'is_on',
    NOT_ON = 'is_not_on',
    AFTER = 'is_after',
    ON_OR_AFTER = 'is_on_or_after',
    BEFORE = 'is_before',
    ON_OR_BEFORE = 'is_on_or_before',
    BETWEEN = 'is_between',
}

export const NUMERICAL_DATE_BUCKETS = [
    ColumnTimeBucket.DayOfWeek,
    ColumnTimeBucket.DayOfMonth,
    ColumnTimeBucket.DayOfQuarter,
    ColumnTimeBucket.DayOfYear,
    ColumnTimeBucket.WeekOfMonth,
    ColumnTimeBucket.WeekOfQuarter,
    ColumnTimeBucket.WeekOfYear,
    ColumnTimeBucket.MonthOfQuarter,
    ColumnTimeBucket.MonthOfYear,
    ColumnTimeBucket.QuarterOfYear,
];

export interface DateFilter {
    startDate?: Date;
    endDate?: Date;
    inputDate?: Date;
    filterOperator?: DateFilterOperators;
}

export const getDateFilterOperatorOptions = (): OptionsGroup[] => [
    {
        title: '',
        options: [
            {
                id: DateFilterOperators.ON,
                label: translate('dateFilter.is_on'),
            },
            // SCAL-91753 Commenting these options until backend provide better support
            // {
            //     id: DateFilterOperators.NOT_ON,
            //     label: translate('dateFilter.is_not_on'),
            // },
        ],
    },
    {
        title: '',
        options: [
            // SCAL-91753 Commenting these options until backend provide better support
            // {
            //     id: DateFilterOperators.AFTER,
            //     label: translate('dateFilter.is_after'),
            // },
            {
                id: DateFilterOperators.ON_OR_AFTER,
                label: translate('dateFilter.is_on_or_after'),
            },
            {
                id: DateFilterOperators.BEFORE,
                label: translate('dateFilter.is_before'),
            },
            // SCAL-91753 Commenting these options until backend provide better support
            // {
            //     id: DateFilterOperators.ON_OR_BEFORE,
            //     label: translate('dateFilter.is_on_or_before'),
            // },
        ],
    },
    {
        title: '',
        options: [
            {
                id: DateFilterOperators.BETWEEN,
                label: translate('dateFilter.is_between'),
            },
        ],
    },
];

export function isStartDatePicker(
    filterOperator: DateFilterOperators,
): boolean {
    return !(
        filterOperator === DateFilterOperators.BEFORE ||
        filterOperator === DateFilterOperators.ON_OR_BEFORE
    );
}

export function showDateRangePicker(
    filterOperator: DateFilterOperators,
): boolean {
    return filterOperator === DateFilterOperators.BETWEEN;
}

function getDateFilterOperator(
    filterType: FilterTypes,
    negate = false,
): DateFilterOperators | null {
    switch (filterType) {
        case FilterTypes.Eq:
            return negate ? DateFilterOperators.NOT_ON : DateFilterOperators.ON;
        case FilterTypes.Lt:
            return DateFilterOperators.BEFORE;
        case FilterTypes.Le:
            return DateFilterOperators.ON_OR_BEFORE;
        case FilterTypes.Gt:
            return DateFilterOperators.AFTER;
        case FilterTypes.Ge:
            return DateFilterOperators.ON_OR_AFTER;
        case FilterTypes.BwIncMin:
            // backend normalizes date filter values to suit FilterTypes.BwIncMin operator
            // Other operators such as BwInc and BwIncMax are not used for date filters
            // so they are not handled
            return DateFilterOperators.BETWEEN;
        default:
            return null;
    }
}

/**
 * If there is more than one date filter applied then it can only be edited
 * using the search bar. Thus we only return the date values from the
 * first element in the filters array
 * @param filters
 */
export function getDateRangeFilterInput(filters: Filter[]) {
    let startDate: Date = null;
    let endDate: Date = null;
    let filterOperator: DateFilterOperators = null;

    if (!_.isEmpty(filters)) {
        // only considering the first element because, if there are
        // more than one date filters applied on a column, they can
        // be edited using search bar.
        const { filterContent } = _.head(filters);
        _.forEach(filterContent, content => {
            filterOperator = getDateFilterOperator(
                content.filterType,
                content.negate,
            );
            if (_.isNil(filterOperator) || _.isEmpty(content.value)) return;
            if (
                [FilterTypes.Ge, FilterTypes.Gt, FilterTypes.Eq].includes(
                    content.filterType,
                )
            ) {
                startDate = getDateFromEpoch(content.value[0].key);
            } else if (
                [FilterTypes.Le, FilterTypes.Lt].includes(content.filterType)
            ) {
                endDate = getDateFromEpoch(content.value[0].key);
            } else if (
                [
                    FilterTypes.Bw,
                    FilterTypes.BwInc,
                    FilterTypes.BwIncMin,
                    FilterTypes.BwIncMax,
                ].includes(content.filterType)
            ) {
                startDate = getDateFromEpoch(content.value[0].key);
                endDate = getDateFromEpoch(content.value[1].key);
            }
        });
    }
    return { startDate, endDate, filterOperator };
}

/**
 * Return false if filter can only be edited using search bar, true otherwise
 * @param filters
 * @param filterColumnDataType
 */
export function canEditFilterUsingWidget(
    filters: Filter[],
    filterColumn: FilterColumn,
) {
    const { dataType, timeBucket } = filterColumn;
    if (![FalconDataType.Date, FalconDataType.DateTime].includes(dataType)) {
        return false;
    }
    if (!_.isEmpty(filters)) {
        const { filterContent } = _.head(filters);
        const content = _.head(filterContent);
        if (_.size(filters) > 1 || _.size(filterContent) > 1) {
            return false;
        }
        if (
            NUMERICAL_DATE_BUCKETS.includes(timeBucket) ||
            (content?.negate && content?.filterType === FilterTypes.BwIncMin)
        ) {
            return false;
        }
    }
    return true;
}

export function getDateRangeFilterContent(
    startDate: Date,
    endDate: Date,
    filterId: string,
    filterOperator: DateFilterOperators,
): FilterContentInput[] | null {
    const startDateEpoch = getEpochFromDate(startDate);
    const endDateEpoch = getEpochFromDate(endDate);
    const filterContent: FilterContentInput = {} as FilterContentInput;

    switch (filterOperator) {
        case DateFilterOperators.NOT_ON:
            if (_.isNil(startDateEpoch)) return null;
            filterContent.filterType = FilterTypes.Ne;
            filterContent.value = [
                { type: ColumnValueType.Date, intValue: startDateEpoch },
            ];
            break;
        case DateFilterOperators.ON:
            if (_.isNil(startDateEpoch)) return null;
            filterContent.filterType = FilterTypes.Eq;
            filterContent.value = [
                { type: ColumnValueType.Date, intValue: startDateEpoch },
            ];
            break;
        case DateFilterOperators.ON_OR_AFTER:
            if (_.isNil(startDateEpoch)) return null;
            filterContent.filterType = FilterTypes.Ge;
            filterContent.value = [
                { type: ColumnValueType.Date, intValue: startDateEpoch },
            ];
            break;
        case DateFilterOperators.AFTER:
            if (_.isNil(startDateEpoch)) return null;
            filterContent.filterType = FilterTypes.Gt;
            filterContent.value = [
                { type: ColumnValueType.Date, intValue: startDateEpoch },
            ];
            break;
        case DateFilterOperators.ON_OR_BEFORE:
            if (_.isNil(endDateEpoch)) return null;
            filterContent.filterType = FilterTypes.Le;
            filterContent.value = [
                { type: ColumnValueType.Date, intValue: endDateEpoch },
            ];
            break;
        case DateFilterOperators.BEFORE:
            if (_.isNil(endDateEpoch)) return null;
            filterContent.filterType = FilterTypes.Lt;
            filterContent.value = [
                { type: ColumnValueType.Date, intValue: endDateEpoch },
            ];
            break;
        case DateFilterOperators.BETWEEN:
            if (_.isNil(startDateEpoch) || _.isNil(endDateEpoch)) return null;
            filterContent.filterType = FilterTypes.BwIncMin;
            filterContent.value = [
                { type: ColumnValueType.Date, intValue: startDateEpoch },
                { type: ColumnValueType.Date, intValue: endDateEpoch },
            ];
            break;
        default:
            // not valid operator
            break;
    }
    if (!_.isNil(filterId)) filterContent.filterId = filterId;

    return [filterContent];
}

export const isDuplicateDateFilter = (
    finalStartDate: Date,
    finalEndDate: Date,
    finalFilterOperator: DateFilterOperators,
    initialStartDate: Date,
    initialEndDate: Date,
    initialFilterOperator: DateFilterOperators,
) => {
    // For empty filter case
    if (
        _.isNil(finalStartDate) &&
        _.isNil(finalEndDate) &&
        _.isNil(initialStartDate) &&
        _.isNil(initialEndDate)
    ) {
        return true;
    }
    if (
        initialFilterOperator &&
        initialFilterOperator === finalFilterOperator
    ) {
        if (initialStartDate) {
            if (initialEndDate) {
                return (
                    finalStartDate?.getTime() === initialStartDate?.getTime() &&
                    finalEndDate?.getTime() === initialEndDate?.getTime()
                );
            }
            return finalStartDate?.getTime() === initialStartDate?.getTime();
        }
        if (initialEndDate) {
            return finalEndDate?.getTime() === initialEndDate?.getTime();
        }
    }
    return false;
};

export const getApplicableDateFilters = (
    filters: Filter[],
    selectedDateFilter: DateFilter,
) => {
    const { startDate, endDate, inputDate, filterOperator } =
        selectedDateFilter || {};
    const filterIds = getAppliedFilterIds(filters);
    const {
        startDate: initStartDate,
        endDate: initEndDate,
        filterOperator: initFilterOperator,
    } = getDateRangeFilterInput(filters);
    let finalStartDate: Date = startDate;
    let finalEndDate: Date = endDate;
    if (filterOperator === DateFilterOperators.BETWEEN) {
        if ((!startDate && endDate) || (startDate && !endDate)) {
            return {
                isInvalid: true,
            };
        }
        if (startDate && endDate && startDate >= endDate) {
            return {
                isInvalid: true,
            };
        }
        finalStartDate = startDate;
        finalEndDate = endDate;
    } else if (isStartDatePicker(filterOperator)) {
        finalStartDate = inputDate;
    } else {
        finalEndDate = inputDate;
    }
    // if initFilterOperator is null that means
    // no filter exist for that column
    if (
        initFilterOperator &&
        isDuplicateDateFilter(
            finalStartDate,
            finalEndDate,
            filterOperator,
            initStartDate,
            initEndDate,
            initFilterOperator,
        )
    ) {
        return {
            filterContent: [],
            removeFilterIds: [],
        };
    }

    let filterContent = getDateRangeFilterContent(
        finalStartDate,
        finalEndDate,
        _.head(filterIds),
        filterOperator,
    );

    const removeFilterIds: string[] =
        _.isEmpty(filterContent) && !_.isEmpty(filterIds) ? filterIds : [];

    if (
        _.isNil(filterContent) &&
        (!_.isEmpty(removeFilterIds) || !initFilterOperator)
    ) {
        filterContent = [
            {
                filterType: FilterTypes.Eq,
                value: [],
            },
        ];
    }
    return {
        filterContent,
        removeFilterIds,
    };
};
